import { useMemo } from 'react';

import { PageConfigMap } from '@/entities/page-config-map';
import { PageUrlKey } from '@/entities/page-url-key';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';

type PageOption = {
  value: PageUrlKey;
  label: string;
};

type PageSections = {
  default: PageOption[];
  main: PageOption[];
};

export function useAvailablePages(): PageSections {
  const featureFlag = useFeatureFlag();
  const me = useMe();

  return useMemo(() => {
    const defaultPages: PageOption[] = [];
    const mainPages: PageOption[] = [];

    // Default section - landing page
    if (featureFlag('landingpage')) {
      defaultPages.push({
        value: PageUrlKey.DEFAULT_LANDING_PAGE,
        label: 'Home',
      });
    }

    // Main pages - only include pages visible in app-sidebar-global.tsx
    // Analyze section order
    if (featureFlag('alerts')) {
      mainPages.push({
        value: PageUrlKey.ALERTS,
        label: PageConfigMap[PageUrlKey.ALERTS].nameFull,
      });
    }

    if (featureFlag('taskmanager')) {
      mainPages.push({
        value: PageUrlKey.TASKS,
        label: PageConfigMap[PageUrlKey.TASKS].nameFull,
      });
    }

    if (featureFlag('assets')) {
      mainPages.push({
        value: PageUrlKey.BENCHMARK,
        label: PageConfigMap[PageUrlKey.BENCHMARK].nameFull,
      });
    }

    if (featureFlag('warranty') && me.config.type === 'mobility') {
      mainPages.push({
        value: PageUrlKey.WARRANTY,
        label: PageConfigMap[PageUrlKey.WARRANTY].nameFull,
      });
    }

    if (featureFlag('dataCompatibility')) {
      mainPages.push({
        value: PageUrlKey.DATA_COMPATIBILITY,
        label: PageConfigMap[PageUrlKey.DATA_COMPATIBILITY].nameFull,
      });
    }

    if (featureFlag('sitemap') && me.config.type !== 'mobility') {
      mainPages.push({
        value: PageUrlKey.SITE_MAP,
        label: PageConfigMap[PageUrlKey.SITE_MAP].nameFull,
      });
    }

    // Organize section order
    if (featureFlag('projectManagement')) {
      mainPages.push({
        value: PageUrlKey.PROJECTS,
        label: PageConfigMap[PageUrlKey.PROJECTS].nameFull,
      });
    }

    if (!me.whiteLabel && featureFlag('communication')) {
      mainPages.push({
        value: PageUrlKey.COMMUNICATION,
        label: PageConfigMap[PageUrlKey.COMMUNICATION].nameFull,
      });
    }

    if (featureFlag('records')) {
      mainPages.push({
        value: PageUrlKey.DOCUMENTS,
        label: PageConfigMap[PageUrlKey.DOCUMENTS].nameFull,
      });
    }

    // Remove duplicates but maintain order
    const uniqueDefaultPages = defaultPages.filter(
      (page, index, self) =>
        index === self.findIndex((p) => p.value === page.value),
    );

    const uniqueMainPages = mainPages.filter(
      (page, index, self) =>
        index === self.findIndex((p) => p.value === page.value),
    );

    return {
      default: uniqueDefaultPages,
      main: uniqueMainPages,
    };
  }, [featureFlag, me]);
}
